import DataService from './DataService';
import TimeStamp from '../utils/TimeStamp';

/**
 * Service de préparation des données pour les documents (devis, factures)
 * Permet de formater les données pour l'affichage et l'export de documents
 */
class DataDocumentService {
    /**
     * Initialise le service avec un objet de données vide
     */
    constructor() {
        this.data = {};
    }

    /**
     * Configure les données du client à partir de son ID
     * @param {string|number} accountId - Identifiant du compte client
     * @returns {Promise<void>}
     */
    async setCustomerData(accountId) {
        const account = await DataService.getAccountWithDetails(accountId);
        if (!account) {
            console.error('Compte non trouvé');
            return;
        }

        this.data = {
            refClient: account.name,
            attention: account.contacts.find(contact => contact.isPrimary)?.firstName + ' ' + account.contacts.find(contact => contact.isPrimary)?.lastName,
            clientName: account.name,
            clientAddress: {
                line1: account.addresses.find(addr => addr.isPrimary)?.address || '',
                line2: `${account.addresses.find(addr => addr.isPrimary)?.postalCode || ''} ${account.addresses.find(addr => addr.isPrimary)?.city || ''}`
            }
        };
    }

    /**
     * Configure les données de facturation du client à partir de son ID
     * Utilise les contacts et adresses marqués comme contacts de facturation
     * @param {string|number} accountId - Identifiant du compte client
     * @returns {Promise<void>}
     */
    async setCustomerBillingData(accountId) {
        const account = await DataService.getAccountWithDetails(accountId);
        if (!account) {
            console.error('Compte non trouvé');
            return;
        }

        // Chercher le contact de facturation, sinon prendre le contact principal
        const billingContact = account.contacts.find(contact => contact.isBillingContact) || 
                             account.contacts.find(contact => contact.isPrimary);

        // Chercher l'adresse de facturation, sinon prendre l'adresse principale
        const billingAddress = account.addresses.find(addr => addr.isBillingLocation) ||
                              account.addresses.find(addr => addr.isPrimary);

        this.data = {
            refClient: account.name,
            attention: billingContact ? `${billingContact.firstName} ${billingContact.lastName}` : '',
            clientName: account.name,
            clientAddress: {
                line1: billingAddress?.address || '',
                line2: `${billingAddress?.postalCode || ''} ${billingAddress?.city || ''}`
            }
        };
    }

    /**
     * Configure les données du devis pour la génération de document
     * @param {Object} quote - Objet devis
     * @param {string} quote.id - Identifiant du devis
     * @param {string} quote.quoteCustomId - Identifiant personnalisé du devis
     * @param {Object} quote.timeStamp - Horodatage du devis
     * @param {string} quote.notes - Notes associées au devis
     * @param {string} quote.delay - Délai proposé
     * @param {string} quote.outOfScope - Éléments hors périmètre
     * @param {string} quote.needsFromClient - Besoins du client
     * @param {Object} opportunity - Opportunité associée au devis
     * @param {string} opportunity.name - Nom de l'opportunité/projet
     * @returns {Promise<void>}
     */
    async setQuoteData(quote, opportunity) {
        try {
            // récupérer l'id du produit dont le nom est OPTIONLINE
            const optionLineProduct = await DataService.getByField('products', 'name', 'OPTIONLINE');

            // Récupérer les lignes du devis
            const quoteWithLines = await DataService.getQuoteWithLines(quote.id);

            // Préparer les données du devis
            this.data.docNumber = quote.quoteCustomId;
            this.data.docType = 'Devis';
            this.data.date = quote.timeStamp ? new TimeStamp(
                quote.timeStamp.year,
                quote.timeStamp.month,
                quote.timeStamp.day,
                quote.timeStamp.hour || 0,
                quote.timeStamp.minute || 0
            ).toFormattedDate() : '';
            this.data.project = opportunity.name;
            this.data.notes = quote.notes ? quote.notes : '';
            this.data.delay = quote.delay ? quote.delay : '';
            this.data.outOfScope = quote.outOfScope ? quote.outOfScope : '';
            this.data.needsFromClient = quote.needsFromClient ? quote.needsFromClient : '';
            this.data.items = quoteWithLines.lines.map(line => ({
                name: line.description,
                price: line.unitPrice * line.quantity + (line.subLines ? line.subLines.reduce((total, subLine) => total + subLine.unitPrice * subLine.quantity, 0) : 0),
                optional: parseInt(line.productId) === parseInt(optionLineProduct.id),
                productId: parseInt(line.productId),
                subItems: line.subLines ? line.subLines.map(subLine => ({
                    name: subLine.description,
                    price: subLine.unitPrice * subLine.quantity,
                    quantity: subLine.quantity,
                    unitPrice: subLine.unitPrice,
                    productId: parseInt(subLine.productId)
                })) : []
            }));

            // Calculer le total du devis hors option
            this.data.total = this.data.items.reduce((total, item) => total + (item.optional ? 0 : item.price), 0);

            // récupérer le paramètre QuoteSubPrices
            const quoteSubPricesSetting = await DataService.getByField('settings', 'name', 'QuoteSubPrices');
            this.data.showSubPrices = quoteSubPricesSetting ? quoteSubPricesSetting.data === 'True' : false;

            // récupérer la TVA20 depuis la base de données
            const vat20Setting = await DataService.getByField('settings', 'name', 'TVA20');
            const vat20 = vat20Setting ? parseFloat(vat20Setting.data) : 0;

            // Calculer le total de la TVA
            this.data.vat = this.data.total * vat20 / 100;

            // Calculer le total du devis avec TVA
            this.data.totalWithVat = this.data.total + this.data.vat;

        } catch (error) {
            console.error('Erreur lors de la génération des données du devis:', error);
        }
    }

    /**
     * Configure les données de facturation pour la génération de document
     * @param {Object} bill - Objet facture
     * @param {string} bill.id - Identifiant de la facture
     * @param {string} bill.billCustomId - Identifiant personnalisé de la facture
     * @param {string} bill.command - Numéro de commande associé
     * @param {Object} bill.timeStamp - Horodatage de la facture
     * @param {Object} bill.dueDate - Date d'échéance de paiement
     * @param {number} bill.totalAmount - Montant total de la facture
     * @param {number} bill.alreadyPaid - Montant déjà payé
     * @param {number} bill.currentProgressAmount - Montant de l'avancement actuel
     * @param {Object} quote - Objet devis associé à la facture
     * @param {string} quote.quoteCustomId - Identifiant personnalisé du devis
     * @param {Object} opportunity - Opportunité associée
     * @param {string} opportunity.name - Nom de l'opportunité/projet
     * @returns {Promise<void>}
     */
    async setBillData(bill, quote, opportunity) {
        try {
            // Récupérer les lignes de la facture
            const billWithLines = await DataService.getBillWithLines(bill.id);

            // Préparer les données de la facture
            this.data.docNumber = bill.billCustomId;
            this.data.quoteNumber = quote.quoteCustomId;
            this.data.docType = 'Facture';
            this.data.command = bill.command;
            this.data.date = bill.timeStamp ? new TimeStamp(
                bill.timeStamp.year,
                bill.timeStamp.month,
                bill.timeStamp.day,
                bill.timeStamp.hour || 0,
                bill.timeStamp.minute || 0
            ).toFormattedDate() : '';
            this.data.dueDate = bill.dueDate ? new TimeStamp(
                bill.dueDate.year,
                bill.dueDate.month,
                bill.dueDate.day,
                bill.dueDate.hour || 0,
                bill.dueDate.minute || 0
            ).toFormattedDate() : '';
            this.data.project = opportunity.name;
            this.data.items = billWithLines.lines.map(line => ({
                name: line.description,
                price: line.price,
                progress: line.progress,
                amount: line.price * line.progress / 100,
                subItems: line.subLines ? line.subLines.map(subLine => ({
                    name: subLine.description
                })) : []
            }));

            // Calculer le total de la facture
            this.data.total = bill.totalAmount;
            this.data.alreadyPaid = bill.alreadyPaid;
            this.data.currentProgressAmount = bill.currentProgressAmount;
            this.data.currentBillAmount = this.data.currentProgressAmount - this.data.alreadyPaid;
            // récupérer la TVA20 depuis la base de données
            const vat20Setting = await DataService.getByField('settings', 'name', 'TVA20');
            const vat20 = vat20Setting ? parseFloat(vat20Setting.data) : 0;

            // Calculer le total de la TVA
            this.data.vat = this.data.currentBillAmount * vat20 / 100;

            // Calculer le total de la facture avec TVA
            this.data.totalWithVat = this.data.currentBillAmount + this.data.vat;
        } catch (error) {
            console.error('Erreur lors de la génération des données de facturation:', error);
        }
    }

    /**
     * Récupère les données formatées d'une facture pour la génération de document
     * @param {string} billCustomId - Identifiant personnalisé de la facture
     * @returns {Promise<Object|undefined>} Données formatées pour le document ou undefined en cas d'erreur
     */
    async getBillDocumentData(billCustomId) {
        try {
            // Récupérer la facture
            const bill = await DataService.getBillByCustomId(billCustomId);
            if (!bill) {
                console.error('Facture non trouvée');
                return;
            }

            const quote = await DataService.getById('quotes', bill.quoteId);
            const opportunity = await DataService.getById('opportunities', quote.opportunityId);

            await this.setCustomerBillingData(opportunity.accountId);  // Utiliser la nouvelle fonction ici
            await this.setBillData(bill, quote, opportunity);

            return this.data;
        } catch (error) {
            console.error('Erreur lors de la génération des données de facturation:', error);
        } 
    }

    /**
     * Récupère les données formatées d'un devis pour la génération de document
     * @param {string} quoteCustomId - Identifiant personnalisé du devis
     * @returns {Promise<Object|undefined>} Données formatées pour le document ou undefined en cas d'erreur
     */
    async getQuoteDocumentData(quoteCustomId) {
        try {
            // Récupérer le devis
            const quote = await DataService.getQuoteByCustomId(quoteCustomId);
            if (!quote) {
                console.error('Devis non trouvé');
                return;
            }
            const opportunity = await DataService.getById('opportunities', quote.opportunityId);

            await this.setCustomerData(opportunity.accountId);
            await this.setQuoteData(quote, opportunity);

            return this.data;
        } catch (error) {
            console.error('Erreur lors de la génération des données du devis:', error);
        } 
    }
}

export default DataDocumentService;